import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Alert } from "react-bootstrap";
import Button from "../../components/Button/Button";
import CardHeader from "../../components/Card/CardHeader";
import Loader from "../../components/Loader/Loader";
import Modal from "../../components/Modal/Modal";
import { termOptionsMap } from "../../utils/helper";
import StudentForm from "./StudentForm/StudentForm";
import StudentSubjectEnrollment from "../StudentSubjectMapping/StudentSubjectEnroll/StudentSubjectEnrollment";
import useStudent from "./hooks/useStudent";
import useBulkUpload from "../../hooks/useBulkUpload";
import useSubjectEnrollment from "../../hooks/useSubjectEnrollment";
import { useSelector, useDispatch } from "react-redux";
import { fetchPrograms } from "../../redux/program/programActions";

const StudentManage = () => {
  const params = useParams();
  const id = params.id;
  const navigate = useNavigate();
  const permissions = JSON.parse(localStorage.getItem("permissions"));
  const dispatch = useDispatch();
  const programData = useSelector(state => state.program.programs.data || []);

  const [isBulk, setIsBulk] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [markedEventMessages, setMarkedEventMessages] = useState([]);

  // --- Single Student Hook ---
  const {
    state,
    setState,
    images,
    setImages,
    errors,
    handleChange,
    handleImageChange,
    onFocusHandler,
    handleSubmit,
    fetchLoading,
    submitLoading,
  } = useStudent(id);
  // --- Bulk Upload Hook ---
  const { handleFile, submitBulkUpload, loading: bulkLoading } = useBulkUpload(programData, !!id);
  // --- Subject Enrollment Hook ---
  const {
    subjectList,
    subjectIds,
    setSubjectIds,
    toggleSubject,
    submitEnrollment,
    subjectsLoading,
    enrolledLoading,
  } = useSubjectEnrollment(state.program_id, id);

  // --- Handlers ---
  const back = () => navigate("/student/list");

  const handleToggleBulk = () => setIsBulk((prev) => !prev);

  const handleModalClose = () => {
    setModalOpen(false);
    setSuccessMessage("");
    setErrorMessage("");
    setMarkedEventMessages([]);
  };

  React.useEffect(() => {
    if (programData.length === 0) dispatch(fetchPrograms());
  }, [dispatch, programData.length]);

  return (
    <div className="card">
      <CardHeader
        title={id ? "Update Student" : "Add Student"}
        button={
          <div>
            {permissions.includes("bulk.upload.student") && !id && (
              <Button variant="primary" className="mx-1" onClick={handleToggleBulk}>
                {isBulk ? "Manual Create" : "Bulk Upload"}
              </Button>
            )}
            <Button variant="secondary" className="mx-1" onClick={back}>
              Back
            </Button>
          </div>
        }
      />

      <Modal isOpen={modalOpen} onClose={handleModalClose} title="Subject and Exam Enrollment">
        <div className="mt-5">
          {errorMessage && (
            <Alert variant="danger">
              <h6>{errorMessage}</h6>
              <ol>
                {markedEventMessages.map((msg, idx) => (
                  <li key={idx}>
                    {msg.event_name} : {msg.subject_name} for {termOptionsMap[msg.term]}
                  </li>
                ))}
              </ol>
            </Alert>
          )}
          {successMessage && <Alert variant="success">{successMessage}</Alert>}
          <div className="d-flex justify-content-end">
            <Button variant="primary" onClick={handleModalClose}>
              OK
            </Button>
          </div>
        </div>
      </Modal>

      <div className="card-body">
        {(fetchLoading || submitLoading || bulkLoading) ? (
          <Loader />
        ) : isBulk ? (
          <StudentForm
            isBulk
            programData={programData}
            bulkUploadFile={submitBulkUpload}
            excelFileReader={handleFile}
            errors={errors}
          />
        ) : (
          <StudentForm
            data={state}
            changeHandler={handleChange}
            submitHandler={handleSubmit}
            handleImageChange={handleImageChange}
            onFocusHandler={onFocusHandler}
            errors={errors}
            images={images}
            setImages={setImages}
            programData={programData}
          />
        )}

        {/* Subject Enrollment Section */}
        {id && !isBulk && (
          <React.Fragment>
            {subjectsLoading || enrolledLoading ? (
              <Loader />
            ) : (
              <React.Fragment>
                <div className="student-form-card student-form-card-indigo mt-3">
                  <div className="student-form-card-header">
                    <span>Subject Enrollment</span>
                  </div>
                </div>
                <StudentSubjectEnrollment
                  student_id={id}
                  program_id={state.program_id}
                  subject_ids={subjectIds}
                  subjectData={subjectList}
                  changeHandler={handleChange}
                  subjectChangeHandler={toggleSubject}
                  selectAllSubjectHandler={(e) => {
                    const allIds = e.target.checked ? subjectList.map((s) => s.id.toString()) : [];
                    setState((prev) => ({ ...prev, subjectIds: allIds }));
                    setSubjectIds(allIds);
                  }}
                  submitHandler={submitEnrollment}
                />
                <div className="d-flex justify-content-end">
                  <Button 
                    variant="primary" 
                    className="mt-3" 
                    onClick={async () => {
                      try {
                        await submitEnrollment();
                        // Success message already shown by useApi hook
                      } catch (error) {
                        // Error already handled by useApi hook
                      }
                    }}
                  >
                    Save
                  </Button>
                </div>
              </React.Fragment>
            )}
          </React.Fragment>
        )}
      </div>
    </div>
  );
};

export default StudentManage;

